import React, { Fragment, useMemo, useState, useEffect, createContext } from "react";
import { Layout, Menu, Drawer, Button } from "antd";
import { dashboardMenus } from "./MainPage.helper";
import Logo from "../../assets/logo/Logo.png";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getFirstSegment } from "../../utils/utils";
import { MenuOutlined, CloseOutlined } from "@ant-design/icons";

const { Content, Sider } = Layout;

const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
};

export const DrawerContext = createContext(null);

const MainPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname.toLowerCase();
  const selected = pathName.substring(11) ? pathName.substring(11) : "dashboard";

  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const isMobile = windowWidth < BREAKPOINTS.mobile;
  const isTablet = windowWidth >= BREAKPOINTS.mobile && windowWidth < BREAKPOINTS.tablet;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= BREAKPOINTS.tablet) {
        setDrawerOpen(false);
        setCollapsed(false);
      } else if (window.innerWidth >= BREAKPOINTS.mobile) {
        setCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const getMenuItems = useMemo(() => {
    if (selected) {
      return dashboardMenus(selected.toLowerCase());
    }
    return [];
  }, [selected]);

  const handleNavigate = (key) => {
    navigate(key?.toLowerCase());
    if (isMobile) setDrawerOpen(false);
  };

  const MenuContent = () => (
    <>
      <div className="sapa-menu__logo">
        {isMobile && (
          <Button
            className="sapa-menu__close-btn"
            icon={<CloseOutlined />}
            onClick={() => setDrawerOpen(false)}
          />
        )}
        <img src={Logo} alt="Company Logo" />
      </div>
      <Menu
        mode="inline"
        className="MainPage__menu"
        selectedKeys={[selected, getFirstSegment(selected)]}
        inlineCollapsed={isTablet && collapsed}
      >
        {getMenuItems.map(({ key, icon, label }) => (
          <Fragment key={key}>
            {[selected, getFirstSegment(selected)].includes(key?.toLowerCase()) ? (
              <span className="hover-bar" />
            ) : (
              <span className="invisible-bar" />
            )}
            <Menu.Item
              className="MainPage__menu_item"
              icon={icon}
              key={key}
              onClick={() => handleNavigate(key)}
            >
              {label}
            </Menu.Item>
          </Fragment>
        ))}
      </Menu>
    </>
  );

  return (
    <Content className="MainPage">
      <Layout className="MainPage__layout">
        {isMobile ? (
          <Drawer
            placement="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            width={260}
            className="MainPage__drawer"
            closeIcon={null}
            styles={{ body: { padding: 0 } }}
          >
            <MenuContent />
          </Drawer>
        ) : (
          <Sider
            className="MainPage__side"
            collapsed={isTablet && collapsed}
            collapsedWidth={72}
            width={260}
            onCollapse={(value) => setCollapsed(value)}
          >
            {isTablet && (
              <Button
                className="MainPage__collapse-btn"
                icon={collapsed ? <MenuOutlined /> : <CloseOutlined />}
                onClick={() => setCollapsed(!collapsed)}
              />
            )}
            <MenuContent />
          </Sider>
        )}

        <Content className="MainPage__content">
          <DrawerContext.Provider value={{ openDrawer: () => setDrawerOpen(true), isMobile }}>
            <Outlet />
          </DrawerContext.Provider>
        </Content>
      </Layout>
    </Content>
  );
};

export default MainPage;